/**
 * Favorites on a project's Home (DashboardView): the "⋯" menu's Add to favorites / Remove from favorites (HomeStrip
 * `stripProps`). Whether it is a favorite is read from the person's favorites (MiaPlatformDataService.getFavorites,
 * the list Projects shows its stars from), so the Projects page reflects a change here the next time it loads.
 */
import { useEffect, useState } from 'react';
import { message } from 'antd';
import miaPlatformDataService from '../../services/MiaPlatformDataService';

export default function useProjectFavorite({ projectId, tenantId = null, enabled = true }) {
  const [favorite, setFavorite] = useState(null); // null until known: no menu entry while it is not
  const [busy, setBusy] = useState(false);
  const on = Boolean(enabled && projectId);

  useEffect(() => {
    setFavorite(null);
    if (!on) { return undefined; }
    let live = true;
    miaPlatformDataService.getFavorites(tenantId)
      .then((ids) => { if (live) { setFavorite((ids || []).includes(projectId)); } })
      .catch(() => { if (live) { setFavorite(null); } });
    return () => { live = false; };
  }, [on, projectId, tenantId]);

  const toggle = async() => {
    if (busy || favorite === null) { return; }
    const was = favorite;
    setBusy(true);
    setFavorite(!was);
    try {
      await miaPlatformDataService.toggleFavorite(projectId);
      message.success(was ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      setFavorite(was);
      message.error(error.message || 'The favorites could not be updated');
    } finally {
      setBusy(false);
    }
  };

  return {
    stripProps: on && favorite !== null ? { favorite, onToggleFavorite: toggle } : {},
  };
}
